import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';

export type ReminderSourceType = 'service' | 'support_agreement' | 'invoice' | 'offer';

export interface UnifiedReminderRow {
  id: string;
  sourceType: ReminderSourceType;
  date: string;
  status: string | null;
  parentDescription: string;
  parentId: number | string;
  clientName: string;
  linkPath: string;
  channel: string | null;
  responseNote: string | null;
  responseAt: string | null;
  createdByLabel: string;
  parentExtraLabel: string;
  parentExtraDate: string | null;
  raw: unknown;
}

interface ServiceReminder {
  id: number;
  sentAt: string;
  channel: string | null;
  renewalResponseStatus: string | null;
  renewalResponseNote: string | null;
  renewalResponseAt: string | null;
  clientService?: {
    clientServicetId: number;
    serviceDescr: string;
    serviceEndDate: string | null;
    client?: { clientName: string };
  };
  createdBy?: { id?: number; userDisplayName?: string | null; userFirstName?: string; userLastName?: string };
}

interface SupportAgreementReminder {
  id: number;
  contactedAt: string;
  channel: string | null;
  responseStatus: string | null;
  responseNote: string | null;
  responseAt: string | null;
  supportAgreement?: {
    id: number;
    description: string;
    endDate: string | null;
    client?: { clientName: string };
  };
  createdBy?: { id?: number; userDisplayName?: string | null; userFirstName?: string; userLastName?: string };
}

interface InvoiceReminder {
  id: number;
  contactedAt: string;
  channel: string | null;
  responseStatus: string | null;
  responseNote: string | null;
  responseAt: string | null;
  invoice?: {
    invoiceId: number;
    InvoiceNo: string;
    invoiceDue: string | null;
    client?: { clientName: string };
  };
  createdBy?: { id?: number; userDisplayName?: string | null; userFirstName?: string; userLastName?: string };
}

interface OfferFollowUp {
  id: number;
  contactedAt: string;
  channel: string | null;
  responseStatus: string | null;
  responseNote: string | null;
  responseAt: string | null;
  offer?: {
    id: number;
    offerNumber: string;
    description: string | null;
    validUntil: string | null;
    client?: { clientName: string };
  };
  createdBy?: { id?: number; userDisplayName?: string | null; userFirstName?: string; userLastName?: string };
}

function createdByLabel(createdBy?: {
  id?: number;
  userDisplayName?: string | null;
  userFirstName?: string;
  userLastName?: string;
}): string {
  if (!createdBy) return '';
  const display = createdBy.userDisplayName?.trim();
  if (display) return display;
  const first = createdBy.userFirstName ?? '';
  const last = createdBy.userLastName ?? '';
  const name = [first, last].filter(Boolean).join(' ').trim();
  return name || (createdBy.id != null ? `User #${createdBy.id}` : '');
}

function normalizeService(rows: ServiceReminder[]): UnifiedReminderRow[] {
  return rows.map((r) => ({
    id: `service-${r.id}`,
    sourceType: 'service' as const,
    date: r.sentAt,
    status: r.renewalResponseStatus ?? null,
    parentDescription: r.clientService?.serviceDescr ?? '',
    parentId: r.clientService?.clientServicetId ?? r.id,
    clientName: r.clientService?.client?.clientName ?? '',
    linkPath: `/client-services/${r.clientService?.clientServicetId ?? ''}`,
    channel: r.channel ?? null,
    responseNote: r.renewalResponseNote ?? null,
    responseAt: r.renewalResponseAt ?? null,
    createdByLabel: createdByLabel(r.createdBy),
    parentExtraLabel: r.clientService?.serviceEndDate ? 'Ends' : '',
    parentExtraDate: r.clientService?.serviceEndDate ?? null,
    raw: r,
  }));
}

function normalizeSupportAgreement(rows: SupportAgreementReminder[]): UnifiedReminderRow[] {
  return rows.map((r) => ({
    id: `support_agreement-${r.id}`,
    sourceType: 'support_agreement' as const,
    date: r.contactedAt,
    status: r.responseStatus ?? null,
    parentDescription: r.supportAgreement?.description ?? '',
    parentId: r.supportAgreement?.id ?? r.id,
    clientName: r.supportAgreement?.client?.clientName ?? '',
    linkPath: `/support-agreements/${r.supportAgreement?.id ?? ''}`,
    channel: r.channel ?? null,
    responseNote: r.responseNote ?? null,
    responseAt: r.responseAt ?? null,
    createdByLabel: createdByLabel(r.createdBy),
    parentExtraLabel: r.supportAgreement?.endDate ? 'Ends' : '',
    parentExtraDate: r.supportAgreement?.endDate ?? null,
    raw: r,
  }));
}

function normalizeInvoice(rows: InvoiceReminder[]): UnifiedReminderRow[] {
  return rows.map((r) => ({
    id: `invoice-${r.id}`,
    sourceType: 'invoice' as const,
    date: r.contactedAt,
    status: r.responseStatus ?? null,
    parentDescription: r.invoice?.InvoiceNo ?? '',
    parentId: r.invoice?.invoiceId ?? r.id,
    clientName: r.invoice?.client?.clientName ?? '',
    linkPath: `/client-invoices/${r.invoice?.invoiceId ?? ''}`,
    channel: r.channel ?? null,
    responseNote: r.responseNote ?? null,
    responseAt: r.responseAt ?? null,
    createdByLabel: createdByLabel(r.createdBy),
    parentExtraLabel: r.invoice?.invoiceDue ? 'Due' : '',
    parentExtraDate: r.invoice?.invoiceDue ?? null,
    raw: r,
  }));
}

function normalizeOffer(rows: OfferFollowUp[]): UnifiedReminderRow[] {
  return rows.map((r) => ({
    id: `offer-${r.id}`,
    sourceType: 'offer' as const,
    date: r.contactedAt,
    status: r.responseStatus ?? null,
    parentDescription: r.offer?.offerNumber ?? r.offer?.description ?? '',
    parentId: r.offer?.id ?? r.id,
    clientName: r.offer?.client?.clientName ?? '',
    linkPath: `/offers/${r.offer?.id ?? ''}`,
    channel: r.channel ?? null,
    responseNote: r.responseNote ?? null,
    responseAt: r.responseAt ?? null,
    createdByLabel: createdByLabel(r.createdBy),
    parentExtraLabel: r.offer?.validUntil ? 'Valid until' : '',
    parentExtraDate: r.offer?.validUntil ?? null,
    raw: r,
  }));
}

export function useAllReminders() {
  const { data: serviceData, error: serviceError, isLoading: serviceLoading, mutate: mutateService } = useSWR<ServiceReminder[]>(
    '/client-service-renewal-reminders/list',
    fetcher,
    { revalidateOnFocus: true }
  );
  const { data: supportData, error: supportError, isLoading: supportLoading, mutate: mutateSupport } = useSWR<SupportAgreementReminder[]>(
    '/support-agreement-renewal-reminders/list',
    fetcher,
    { revalidateOnFocus: true }
  );
  const { data: invoiceData, error: invoiceError, isLoading: invoiceLoading, mutate: mutateInvoice } = useSWR<InvoiceReminder[]>(
    '/client-invoice-payment-reminders/list',
    fetcher,
    { revalidateOnFocus: true }
  );
  const { data: offerData, error: offerError, isLoading: offerLoading, mutate: mutateOffer } = useSWR<OfferFollowUp[]>(
    '/offer-follow-ups/list',
    fetcher,
    { revalidateOnFocus: true }
  );

  const rows: UnifiedReminderRow[] = [];
  if (Array.isArray(serviceData)) rows.push(...normalizeService(serviceData));
  if (Array.isArray(supportData)) rows.push(...normalizeSupportAgreement(supportData));
  if (Array.isArray(invoiceData)) rows.push(...normalizeInvoice(invoiceData));
  if (Array.isArray(offerData)) rows.push(...normalizeOffer(offerData));

  rows.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const isLoading = serviceLoading || supportLoading || invoiceLoading || offerLoading;
  const isError = serviceError || supportError || invoiceError || offerError;
  const mutate = () => {
    mutateService();
    mutateSupport();
    mutateInvoice();
    mutateOffer();
  };

  return {
    data: rows,
    rows,
    total: rows.length,
    isLoading,
    isError,
    mutate,
  };
}

export const SOURCE_LABELS: Record<ReminderSourceType, string> = {
  service: 'Service renewal',
  support_agreement: 'Support agreement',
  invoice: 'Invoice payment',
  offer: 'Offer follow-up',
};
